/**
 * Schema Zod do formulário de CategoriaLiga — mirror de
 * `schemas/category/categorySchemas.ts` (editCategorySchema), restrito aos campos-
 * template (sem datas/status/vagas/aplicarDescontoTorneio/configFilaEspera, que são
 * per-torneio).
 *
 * PRPs/plans/categoria-liga-phase-3-frontend.plan.md — Task 3 (Satisfies AC-A1).
 * Colocado em `src/schemas/categoriaLiga/` (não `src/validation/`) para ficar em
 * lockstep com o sibling real que reutiliza (enums + `getValidTiebreakValues`) — ver
 * "Notes" do plano.
 */
import { z } from 'zod';
import { modeloDisputaEnum } from '../category/categorySchemas';
import { TipoPontuacao } from '../../types/categoriaTorneio';
import { NivelCategoria } from '../../types/categoriaLiga';
import { getValidTiebreakValues } from '../../utils/tiebreakOptions';
import type { CategoriaLigaInput } from '../../types/categoriaLiga';

export const categoriaLigaSchema = z
  .object({
    nome: z.string().min(3, 'Nome obrigatório'),
    descricao: z.string().optional(),
    genero: z.string(),
    // AC-2: obrigatório em runtime — mirror invertido de `tipoPontuacao`, mensagem em
    // Português. Vale para criação e edição (AC-3): mesmo schema nos dois fluxos, a
    // obrigatoriedade nunca é relaxada condicionalmente. `.optional()` aqui é só para o
    // TIPO TypeScript inferido aceitar a chave ausente (ex.: um form ainda sem nenhum
    // valor prévio) — o preprocess normaliza `''` (select vazio) para `undefined` e o
    // `z.nativeEnum` interno (sem `.optional()`) é quem de fato barra o submit com
    // `required_error`, porque o bypass do `.optional()` externo só ocorre quando o
    // valor BRUTO já é `undefined`, nunca quando é a string vazia do select.
    nivel: z
      .preprocess(
        (val) => (val === '' ? undefined : val),
        z.nativeEnum(NivelCategoria, {
          required_error: 'Nível é obrigatório',
          invalid_type_error: 'Nível é obrigatório',
        }),
      )
      .optional(),
    // AC-16/AC-A4: lista explícita de ids marcados — os atalhos da UI só alteram este
    // array em memória, nenhuma "regra" é persistida. Opcional (não `.default([])`) para
    // que um form sem nenhuma seleção (ou fixtures que não fixam o campo) permaneça
    // `undefined` em vez de forçar o valor a existir no tipo de saída.
    combinaveisIds: z.array(z.string()).optional(),
    idadeInicial: z
      .number({ invalid_type_error: 'Idade inicial inválida' })
      .int('Idade inicial deve ser inteira')
      .nonnegative('Idade inicial não pode ser negativa')
      .optional()
      .or(z.nan().transform(() => undefined)),
    idadeFinal: z
      .number({ invalid_type_error: 'Idade final inválida' })
      .int('Idade final deve ser inteira')
      .nonnegative('Idade final não pode ser negativa')
      .optional()
      .or(z.nan().transform(() => undefined)),
    idadeCombinada: z
      .number({ invalid_type_error: 'Idade combinada inválida' })
      .int('Idade combinada deve ser inteira')
      .nonnegative('Idade combinada não pode ser negativa')
      .optional()
      .or(z.nan().transform(() => undefined)),
    modeloDisputa: z.preprocess(
      (val) => (val === '' ? undefined : val),
      z.union([modeloDisputaEnum, z.string()]).optional(),
    ),
    tipoPontuacao: z.preprocess(
      (val) => (val === '' ? undefined : val),
      z.nativeEnum(TipoPontuacao).optional(),
    ),
    numeroSets: z
      .number({ invalid_type_error: 'Número de sets inválido' })
      .int('Número de sets deve ser inteiro')
      .min(1, 'Número de sets deve ser entre 1 e 5')
      .max(5, 'Número de sets deve ser entre 1 e 5')
      .optional()
      .or(z.nan().transform(() => undefined)),
    gamesPorSets: z
      .number({ invalid_type_error: 'Games por set inválido' })
      .int('Games por set deve ser inteiro')
      .min(1, 'Games por set deve ser entre 1 e 11')
      .max(11, 'Games por set deve ser entre 1 e 11')
      .optional()
      .or(z.nan().transform(() => undefined)),
    tiebreaks: z
      .number({ invalid_type_error: 'Tiebreak inválido' })
      .int('Tiebreak deve ser inteiro')
      .min(1, 'Tiebreak deve ser entre 1 e 11')
      .max(11, 'Tiebreak deve ser entre 1 e 11')
      .optional()
      .or(z.nan().transform(() => undefined)),
    // .nonnegative() (não .positive()): 0 é valor de domínio legítimo para modelos
    // de grupo fixo (GRUPO_UNICO/REI_RAINHA/SUPER_8) — mesma regra de categorySchemas.ts.
    quantidadeGrupos: z
      .number({ invalid_type_error: 'Quantidade de grupos inválida' })
      .int('Quantidade de grupos deve ser inteira')
      .positive('Quantidade de grupos deve ser maior que 0')
      .optional()
      .or(z.nan().transform(() => undefined)),
    classificadosPorGrupo: z
      .number({ invalid_type_error: 'Classificados por grupo inválido' })
      .int('Classificados por grupo deve ser inteiro')
      .nonnegative('Classificados por grupo não pode ser negativo')
      .optional()
      .or(z.nan().transform(() => undefined)),
    tamanhoGrupo: z
      .number({ invalid_type_error: 'Tamanho de grupo inválido' })
      .int('Tamanho de grupo deve ser inteiro')
      .positive('Tamanho de grupo deve ser maior que 0')
      .optional()
      .or(z.nan().transform(() => undefined)),
    precoInscricao: z
      .number({ invalid_type_error: 'Preço inválido' })
      .nonnegative('Preço não pode ser negativo'),
    numeroAtletas: z
      .number({ invalid_type_error: 'Número de atletas inválido' })
      .int('Número de atletas deve ser inteiro')
      .positive('Número de atletas deve ser maior que 0'),
  })
  .superRefine((data, ctx) => {
    // Checagem cruzada idêntica a editCategorySchema: o tiebreak precisa ser uma
    // das opções válidas para o games por set escolhido.
    if (
      data.tiebreaks != null &&
      data.gamesPorSets != null &&
      !getValidTiebreakValues(data.gamesPorSets).includes(data.tiebreaks)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tiebreaks'],
        message: `Tiebreak inválido para ${data.gamesPorSets} games por set.`,
      });
    }

    // GRUPOS_ELIMINACAO exige ao menos 1 classificado por grupo (mesma regra de
    // editCategorySchema).
    if (
      data.modeloDisputa === 'GRUPOS_ELIMINACAO' &&
      (data.classificadosPorGrupo == null || data.classificadosPorGrupo < 1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['classificadosPorGrupo'],
        message: 'Classificados por grupo deve ser maior que 0.',
      });
    }
  });

export type CategoriaLigaFormValues = z.infer<typeof categoriaLigaSchema>;

export const toCategoriaLigaInput = (
  values: CategoriaLigaFormValues,
  ligaId: string,
): CategoriaLigaInput => ({
  ligaId,
  nome: values.nome,
  descricao: values.descricao?.trim() || undefined,
  genero: values.genero,
  // Não-nulo por construção: o schema Zod (`required_error`) barra o submit sem `nivel`
  // selecionado — `toCategoriaLigaInput` só roda depois da validação passar.
  nivel: values.nivel as NivelCategoria,
  combinaveisIds: values.combinaveisIds,
  idadeInicial: values.idadeInicial,
  idadeFinal: values.idadeFinal,
  idadeCombinada: values.idadeCombinada,
  modeloDisputa: values.modeloDisputa,
  tipoPontuacao: values.tipoPontuacao,
  numeroSets: values.numeroSets,
  gamesPorSets: values.gamesPorSets,
  tiebreaks: values.tiebreaks,
  quantidadeGrupos: values.quantidadeGrupos,
  classificadosPorGrupo: values.classificadosPorGrupo,
  tamanhoGrupo: values.tamanhoGrupo,
  precoInscricao: values.precoInscricao,
  numeroAtletas: values.numeroAtletas,
});
